import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, Shield, Ban, Check, Trash2, RotateCcw, 
  Filter, ChevronDown, ExternalLink, Image, Clock, User,
  Loader2, RefreshCw, AlertCircle, X, FileWarning, UserX,
  TrendingDown, Eye, Users, Activity, Scale, ChevronRight,
  AlertOctagon, Pause, History, MessageSquare, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import { toAbsoluteUrl } from '../lib/media';
import './AdminSafetyCenter.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = {
  prohibited: { en: 'Prohibited', ar: 'ممنوع' },
  scam: { en: 'Scam', ar: 'احتيال' },
  wrong_category: { en: 'Wrong Category', ar: 'تصنيف خاطئ' },
  spam: { en: 'Spam', ar: 'مزعج' },
  offensive: { en: 'Offensive', ar: 'مسيء' },
  counterfeit: { en: 'Counterfeit', ar: 'مقلد' },
  other: { en: 'Other', ar: 'أخرى' },
};

const STATUS_LABELS = {
  pending: { en: 'Pending', ar: 'قيد الانتظار', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  under_review: { en: 'Under Review', ar: 'قيد المراجعة', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  resolved_kept: { en: 'Kept', ar: 'تم الاحتفاظ', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  resolved_removed: { en: 'Removed', ar: 'تمت الإزالة', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  resolved_ban: { en: 'User Banned', ar: 'تم الحظر', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  dismissed: { en: 'Dismissed', ar: 'مرفوض', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700/30 dark:text-gray-400' },
  resolved_false: { en: 'False Report', ar: 'بلاغ كاذب', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
};

const TRUST_LEVEL_COLORS = {
  healthy: 'text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400',
  warning: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400',
  cooldown: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20 dark:text-orange-400',
  suspension: 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400',
  ban: 'text-purple-600 bg-purple-50 dark:bg-purple-900/20 dark:text-purple-400',
};

function AdminSafetyCenter() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, reports, cases, appeals

  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // Reports data (existing functionality)
  const [reportedAds, setReportedAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [badgeFilter, setBadgeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Report review modal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(null);

  // Pending bans
  const [pendingBans, setPendingBans] = useState([]);
  const [showPendingBans, setShowPendingBans] = useState(false);
  const [banActionLoading, setBanActionLoading] = useState(null);

  // Cases data (new)
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesTotal, setCasesTotal] = useState(0);
  const [casesSkip, setCasesSkip] = useState(0);
  const [caseStatusFilter, setCaseStatusFilter] = useState('');
  const [caseTrustFilter, setCaseTrustFilter] = useState('');

  // Case detail drawer
  const [selectedCase, setSelectedCase] = useState(null);
  const [caseDetailOpen, setCaseDetailOpen] = useState(false);
  const [caseDetailLoading, setCaseDetailLoading] = useState(false);
  const [caseActionLoading, setCaseActionLoading] = useState(false);

  // Appeals data (new)
  const [appeals, setAppeals] = useState([]);
  const [appealsLoading, setAppealsLoading] = useState(false);
  const [appealsTotal, setAppealsTotal] = useState(0);
  const [appealsSkip, setAppealsSkip] = useState(0);
  const [appealStatusFilter, setAppealStatusFilter] = useState('open');

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/safety/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const fetchReportedAds = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('skip', skip.toString());
      params.append('limit', limit.toString());
      if (statusFilter) params.append('status', statusFilter);
      if (reasonFilter) params.append('reason', reasonFilter);
      if (badgeFilter) params.append('badge', badgeFilter);

      const response = await fetch(`${API_URL}/api/admin/safety-center?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 403) {
          navigate('/account');
          return;
        }
        throw new Error('Failed to fetch reported ads');
      }

      const data = await response.json();
      setReportedAds(data.reported_ads || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
      toast.error(isArabic ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [skip, statusFilter, reasonFilter, badgeFilter, navigate, isArabic]);

  // Fetch cases
  const fetchCases = useCallback(async () => {
    setCasesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('skip', casesSkip.toString());
      params.append('limit', '20');
      if (caseStatusFilter) params.append('status', caseStatusFilter);
      if (caseTrustFilter) params.append('trust_level', caseTrustFilter);

      const response = await fetch(`${API_URL}/api/admin/safety/cases?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setCases(data.cases || []);
        setCasesTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Cases fetch error:', err);
    } finally {
      setCasesLoading(false);
    }
  }, [casesSkip, caseStatusFilter, caseTrustFilter]);

  // Fetch appeals
  const fetchAppeals = useCallback(async () => {
    setAppealsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('skip', appealsSkip.toString());
      params.append('limit', '20');
      if (appealStatusFilter) params.append('status', appealStatusFilter);

      const response = await fetch(`${API_URL}/api/admin/safety/appeals?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setAppeals(data.appeals || []);
        setAppealsTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Appeals fetch error:', err);
    } finally {
      setAppealsLoading(false);
    }
  }, [appealsSkip, appealStatusFilter]);

  // Fetch case detail
  const fetchCaseDetail = async (userId) => {
    setCaseDetailLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/safety/case/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedCase(data);
        setCaseDetailOpen(true);
      }
    } catch (err) {
      toast.error('Failed to load case details');
    } finally {
      setCaseDetailLoading(false);
    }
  };

  // Case action
  const handleCaseAction = async (userId, action, reasonCode = null, note = null, durationHours = null) => {
    setCaseActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('action', action);
      if (reasonCode) formData.append('reason_code', reasonCode);
      if (note) formData.append('note', note);
      if (durationHours) formData.append('duration_hours', durationHours.toString());

      const response = await fetch(`${API_URL}/api/admin/safety/case/${userId}/action`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(isArabic ? 'تم تنفيذ الإجراء' : 'Action completed');
        fetchCases();
        fetchDashboard();
        if (selectedCase) {
          fetchCaseDetail(userId);
        }
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCaseActionLoading(false);
    }
  };

  // Appeal action
  const handleAppealAction = async (appealId, decision, note = null) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('decision', decision);
      if (note) formData.append('note', note);

      const response = await fetch(`${API_URL}/api/admin/safety/appeals/${appealId}/review`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(decision === 'accepted' 
          ? (isArabic ? 'تم قبول الاستئناف' : 'Appeal accepted')
          : (isArabic ? 'تم رفض الاستئناف' : 'Appeal rejected')
        );
        fetchAppeals();
        fetchDashboard();
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchReportedAds();
    fetchPendingBans();
  }, [fetchDashboard, fetchReportedAds]);

  useEffect(() => {
    if (activeTab === 'reports') {
      fetchReportedAds();
    } else if (activeTab === 'cases') {
      fetchCases();
    } else if (activeTab === 'appeals') {
      fetchAppeals();
    }
  }, [activeTab, fetchReportedAds, fetchCases, fetchAppeals]);

  const handleAction = async (adId, action, note = '') => {
    setActionLoading(adId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/safety-center/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ad_id: adId, action, note }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(
          isArabic 
            ? `تم تنفيذ الإجراء بنجاح` 
            : `Action completed successfully`
        );
        fetchReportedAds();
        fetchDashboard();
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  // Fetch pending bans
  const fetchPendingBans = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/pending-bans`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingBans(data.pending_bans || []);
      }
    } catch (err) {
      console.error('Failed to fetch pending bans:', err);
    }
  }, []);

  // Review individual report
  const handleReviewReport = async (reportId, verdict, note = '') => {
    setReviewLoading(reportId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/reports/${reportId}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ report_id: reportId, verdict, note }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(
          verdict === 'dismissed'
            ? (isArabic ? 'تم رفض البلاغ' : 'Report dismissed')
            : (isArabic ? 'تم تطبيق الإنذار على المُبلِّغ' : 'Strike applied to reporter')
        );
        fetchReportedAds();
        if (data.strike_applied) {
          fetchPendingBans();
        }
      } else {
        throw new Error(data.detail || 'Review failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReviewLoading(null);
    }
  };

  // Confirm or reject ban
  const handleBanConfirmation = async (userId, confirm, note = '') => {
    setBanActionLoading(userId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/confirm-ban`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, confirm, note }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        toast.success(
          confirm
            ? (isArabic ? 'تم حظر المستخدم' : 'User banned')
            : (isArabic ? 'تم إلغاء الحظر' : 'Ban rejected')
        );
        fetchPendingBans();
        fetchDashboard();
      } else {
        throw new Error(data.detail || 'Action failed');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBanActionLoading(null);
    }
  };

  const openReportReview = (ad) => {
    setSelectedAd(ad);
    setReviewModalOpen(true);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/api/')) return `${API_URL}${imagePath}`;
    if (imagePath.startsWith('/uploads/')) return `${API_URL}/api${imagePath}`;
    return imagePath;
  };

  const getBadgeStyle = (badge) => {
    if (badge === 'auto_hidden') {
      return 'bg-red-500 text-white';
    }
    return 'bg-amber-500 text-white';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(isArabic ? 'ar-SD' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Dashboard Tab
  const renderDashboard = () => (
    <div className="safety-dashboard" data-testid="safety-dashboard">
      {dashboardLoading ? (
        <div className="dashboard-loading">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : dashboard ? (
        <>
          {/* Metrics Grid */}
          <div className="metrics-grid">
            <div className="metric-card critical" data-testid="metric-pending-bans">
              <div className="metric-icon">
                <Ban size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.pending_bans}</span>
                <span className="metric-label">{isArabic ? 'حظر معلق' : 'Pending Bans'}</span>
              </div>
            </div>
            
            <div className="metric-card warning" data-testid="metric-pending-appeals">
              <div className="metric-icon">
                <Scale size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.pending_appeals}</span>
                <span className="metric-label">{isArabic ? 'استئنافات معلقة' : 'Pending Appeals'}</span>
              </div>
            </div>
            
            <div className="metric-card info" data-testid="metric-low-trust">
              <div className="metric-icon">
                <TrendingDown size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.low_trust_users}</span>
                <span className="metric-label">{isArabic ? 'ثقة منخفضة' : 'Low Trust Users'}</span>
              </div>
            </div>
            
            <div className="metric-card" data-testid="metric-daily-enforcements">
              <div className="metric-icon">
                <Activity size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.daily_enforcements}</span>
                <span className="metric-label">{isArabic ? 'إجراءات اليوم' : 'Daily Actions'}</span>
              </div>
            </div>
            
            <div className="metric-card" data-testid="metric-reported-ads">
              <div className="metric-icon">
                <AlertTriangle size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.reported_ads}</span>
                <span className="metric-label">{isArabic ? 'إعلانات مبلغ عنها' : 'Reported Ads'}</span>
              </div>
            </div>
            
            <div className="metric-card danger" data-testid="metric-auto-hidden">
              <div className="metric-icon">
                <Eye size={24} />
              </div>
              <div className="metric-content">
                <span className="metric-value">{dashboard.metrics.auto_hidden_ads}</span>
                <span className="metric-label">{isArabic ? 'مخفي تلقائياً' : 'Auto-Hidden'}</span>
              </div>
            </div>
          </div>

          {/* Trust Distribution */}
          <div className="trust-distribution-card" data-testid="trust-distribution">
            <h3 className="card-title">
              <BarChart3 size={20} />
              {isArabic ? 'توزيع درجات الثقة' : 'Trust Score Distribution'}
            </h3>
            <div className="trust-bars">
              {Object.entries(dashboard.trust_distribution).map(([level, count]) => (
                <div key={level} className="trust-bar-row">
                  <span className={`trust-level-label ${TRUST_LEVEL_COLORS[level]}`}>
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </span>
                  <div className="trust-bar-container">
                    <div 
                      className={`trust-bar trust-bar-${level}`}
                      style={{ 
                        width: `${Math.min(100, (count / Math.max(1, Object.values(dashboard.trust_distribution).reduce((a,b) => a+b, 0))) * 100)}%` 
                      }}
                    />
                  </div>
                  <span className="trust-count">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-card">
            <h3 className="card-title">
              {isArabic ? 'إجراءات سريعة' : 'Quick Actions'}
            </h3>
            <div className="quick-actions-grid">
              <button 
                className="quick-action-btn"
                onClick={() => setActiveTab('reports')}
                data-testid="goto-reports"
              >
                <AlertTriangle size={20} />
                <span>{isArabic ? 'مراجعة البلاغات' : 'Review Reports'}</span>
                <span className="badge">{dashboard.metrics.reported_ads}</span>
              </button>
              
              <button 
                className="quick-action-btn"
                onClick={() => setActiveTab('cases')}
                data-testid="goto-cases"
              >
                <Users size={20} />
                <span>{isArabic ? 'حالات المستخدمين' : 'User Cases'}</span>
                <span className="badge">{dashboard.metrics.low_trust_users}</span>
              </button>
              
              <button 
                className="quick-action-btn"
                onClick={() => setActiveTab('appeals')}
                data-testid="goto-appeals"
              >
                <Scale size={20} />
                <span>{isArabic ? 'مراجعة الاستئنافات' : 'Review Appeals'}</span>
                <span className="badge">{dashboard.metrics.pending_appeals}</span>
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );

  // Cases Tab
  const renderCases = () => (
    <div className="safety-cases" data-testid="safety-cases">
      {/* Case Filters */}
      <div className="case-filters">
        <select 
          value={caseStatusFilter} 
          onChange={(e) => { setCaseStatusFilter(e.target.value); setCasesSkip(0); }}
          data-testid="case-status-filter"
        >
          <option value="">{isArabic ? 'كل الحالات' : 'All Statuses'}</option>
          <option value="active">{isArabic ? 'نشط' : 'Active'}</option>
          <option value="suspended">{isArabic ? 'موقوف' : 'Suspended'}</option>
          <option value="pending_ban">{isArabic ? 'حظر معلق' : 'Pending Ban'}</option>
          <option value="banned">{isArabic ? 'محظور' : 'Banned'}</option>
        </select>
        
        <select 
          value={caseTrustFilter} 
          onChange={(e) => { setCaseTrustFilter(e.target.value); setCasesSkip(0); }}
          data-testid="case-trust-filter"
        >
          <option value="">{isArabic ? 'كل مستويات الثقة' : 'All Trust Levels'}</option>
          <option value="warning">{isArabic ? 'تحذير (51-70)' : 'Warning (51-70)'}</option>
          <option value="cooldown">{isArabic ? 'تبريد (31-50)' : 'Cooldown (31-50)'}</option>
          <option value="suspension">{isArabic ? 'إيقاف (11-30)' : 'Suspension (11-30)'}</option>
          <option value="ban">{isArabic ? 'حظر (0-10)' : 'Ban (0-10)'}</option>
        </select>
      </div>

      {/* Cases List */}
      {casesLoading ? (
        <div className="safety-loading">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : cases.length === 0 ? (
        <div className="safety-empty">
          <Users size={48} />
          <h3>{isArabic ? 'لا توجد حالات' : 'No Cases'}</h3>
          <p>{isArabic ? 'لا توجد حالات مستخدمين تحتاج مراجعة' : 'No user cases need review'}</p>
        </div>
      ) : (
        <div className="cases-list">
          {cases.map((caseItem) => (
            <div 
              key={caseItem.user_id} 
              className="case-card"
              onClick={() => fetchCaseDetail(caseItem.user_id)}
              data-testid={`case-${caseItem.user_id}`}
            >
              <div className="case-header">
                <div className="case-user-info">
                  <span className="case-user-name">{caseItem.name || caseItem.email}</span>
                  <span className={`trust-level-badge ${TRUST_LEVEL_COLORS[caseItem.trust_level]}`}>
                    {caseItem.trust_level}
                  </span>
                </div>
                <div className="case-trust-score">
                  <span className={`trust-score-value ${caseItem.trust_score < 50 ? 'low' : ''}`}>
                    {caseItem.trust_score}
                  </span>
                </div>
              </div>
              
              <div className="case-stats">
                <span className="stat">
                  <AlertTriangle size={14} />
                  {caseItem.enforcement_count} {isArabic ? 'إجراءات' : 'actions'}
                </span>
                <span className="stat">
                  <History size={14} />
                  {caseItem.trust_event_count} {isArabic ? 'أحداث' : 'events'}
                </span>
                {caseItem.appeal_count > 0 && (
                  <span className="stat">
                    <Scale size={14} />
                    {caseItem.appeal_count} {isArabic ? 'استئنافات' : 'appeals'}
                  </span>
                )}
              </div>
              
              <div className="case-status">
                {caseItem.account_status === 'suspended' && (
                  <span className="status-badge suspended">
                    <Pause size={12} />
                    {isArabic ? 'موقوف' : 'Suspended'}
                  </span>
                )}
                {caseItem.account_status === 'pending_ban' && (
                  <span className="status-badge pending-ban">
                    <AlertOctagon size={12} />
                    {isArabic ? 'حظر معلق' : 'Pending Ban'}
                  </span>
                )}
                {caseItem.is_banned && (
                  <span className="status-badge banned">
                    <Ban size={12} />
                    {isArabic ? 'محظور' : 'Banned'}
                  </span>
                )}
              </div>
              
              <ChevronRight size={18} className="case-arrow" />
            </div>
          ))}
        </div>
      )}

      {/* Cases Pagination */}
      {casesTotal > 20 && (
        <div className="safety-pagination">
          <button
            onClick={() => setCasesSkip(Math.max(0, casesSkip - 20))}
            disabled={casesSkip === 0}
            className="pagination-btn"
          >
            {isArabic ? 'السابق' : 'Previous'}
          </button>
          <span className="pagination-info">
            {casesSkip + 1} - {Math.min(casesSkip + 20, casesTotal)} {isArabic ? 'من' : 'of'} {casesTotal}
          </span>
          <button
            onClick={() => setCasesSkip(casesSkip + 20)}
            disabled={casesSkip + 20 >= casesTotal}
            className="pagination-btn"
          >
            {isArabic ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );

  // Appeals Tab
  const renderAppeals = () => (
    <div className="safety-appeals" data-testid="safety-appeals">
      {/* Appeal Filters */}
      <div className="appeal-filters">
        <select 
          value={appealStatusFilter} 
          onChange={(e) => { setAppealStatusFilter(e.target.value); setAppealsSkip(0); }}
          data-testid="appeal-status-filter"
        >
          <option value="open">{isArabic ? 'مفتوح' : 'Open'}</option>
          <option value="reviewing">{isArabic ? 'قيد المراجعة' : 'Reviewing'}</option>
          <option value="accepted">{isArabic ? 'مقبول' : 'Accepted'}</option>
          <option value="rejected">{isArabic ? 'مرفوض' : 'Rejected'}</option>
        </select>
      </div>

      {/* Appeals List */}
      {appealsLoading ? (
        <div className="safety-loading">
          <Loader2 className="animate-spin" size={32} />
        </div>
      ) : appeals.length === 0 ? (
        <div className="safety-empty">
          <Scale size={48} />
          <h3>{isArabic ? 'لا توجد استئنافات' : 'No Appeals'}</h3>
          <p>{isArabic ? 'لا توجد استئنافات بهذه الحالة' : 'No appeals with this status'}</p>
        </div>
      ) : (
        <div className="appeals-list">
          {appeals.map((appeal) => (
            <div key={appeal.appeal_id} className="appeal-card" data-testid={`appeal-${appeal.appeal_id}`}>
              <div className="appeal-header">
                <div className="appeal-user-info">
                  <User size={16} />
                  <span>{appeal.user?.name || appeal.user?.email || 'Unknown'}</span>
                </div>
                <span className="appeal-date">{formatDate(appeal.created_at)}</span>
              </div>
              
              <div className="appeal-message">
                <MessageSquare size={14} />
                <p>{appeal.message}</p>
              </div>
              
              {appeal.case_id && (
                <div className="appeal-case-id">
                  Case: {appeal.case_id}
                </div>
              )}
              
              {appeal.status === 'open' && (
                <div className="appeal-actions">
                  <button
                    onClick={() => handleAppealAction(appeal.appeal_id, 'accepted')}
                    className="appeal-btn accept"
                    data-testid={`accept-appeal-${appeal.appeal_id}`}
                  >
                    <Check size={16} />
                    {isArabic ? 'قبول' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleAppealAction(appeal.appeal_id, 'rejected')}
                    className="appeal-btn reject"
                    data-testid={`reject-appeal-${appeal.appeal_id}`}
                  >
                    <X size={16} />
                    {isArabic ? 'رفض' : 'Reject'}
                  </button>
                </div>
              )}
              
              {appeal.status !== 'open' && (
                <div className={`appeal-status-badge ${appeal.status}`}>
                  {appeal.status === 'accepted' ? (isArabic ? 'مقبول' : 'Accepted') : 
                   appeal.status === 'rejected' ? (isArabic ? 'مرفوض' : 'Rejected') : appeal.status}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Appeals Pagination */}
      {appealsTotal > 20 && (
        <div className="safety-pagination">
          <button
            onClick={() => setAppealsSkip(Math.max(0, appealsSkip - 20))}
            disabled={appealsSkip === 0}
            className="pagination-btn"
          >
            {isArabic ? 'السابق' : 'Previous'}
          </button>
          <span className="pagination-info">
            {appealsSkip + 1} - {Math.min(appealsSkip + 20, appealsTotal)} {isArabic ? 'من' : 'of'} {appealsTotal}
          </span>
          <button
            onClick={() => setAppealsSkip(appealsSkip + 20)}
            disabled={appealsSkip + 20 >= appealsTotal}
            className="pagination-btn"
          >
            {isArabic ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );

  // Reports Tab (existing functionality)
  const renderReports = () => (
    <div className="safety-reports" data-testid="safety-reports">
      {/* Filters */}
      <div className="safety-filters">
        <button 
          className="filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
          data-testid="filter-toggle"
        >
          <Filter size={18} />
          <span>{isArabic ? 'فلترة' : 'Filters'}</span>
          <ChevronDown size={16} className={showFilters ? 'rotate-180' : ''} />
        </button>

        {showFilters && (
          <div className="filter-options">
            <div className="filter-group">
              <label>{isArabic ? 'الحالة' : 'Badge'}</label>
              <select 
                value={badgeFilter} 
                onChange={(e) => { setBadgeFilter(e.target.value); setSkip(0); }}
                data-testid="badge-filter"
              >
                <option value="">{isArabic ? 'الكل' : 'All'}</option>
                <option value="suspicious">{isArabic ? 'مشبوه (1-2 بلاغات)' : 'Suspicious (1-2 reports)'}</option>
                <option value="auto_hidden">{isArabic ? 'مخفي تلقائياً (3+ بلاغات)' : 'Auto-hidden (3+ reports)'}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{isArabic ? 'السبب' : 'Reason'}</label>
              <select 
                value={reasonFilter} 
                onChange={(e) => { setReasonFilter(e.target.value); setSkip(0); }}
                data-testid="reason-filter"
              >
                <option value="">{isArabic ? 'الكل' : 'All'}</option>
                {Object.entries(REPORT_REASONS).map(([key, labels]) => (
                  <option key={key} value={key}>
                    {isArabic ? labels.ar : labels.en}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>{isArabic ? 'حالة البلاغ' : 'Report Status'}</label>
              <select 
                value={statusFilter} 
                onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
                data-testid="status-filter"
              >
                <option value="">{isArabic ? 'الكل' : 'All'}</option>
                {Object.entries(STATUS_LABELS).map(([key, labels]) => (
                  <option key={key} value={key}>
                    {isArabic ? labels.ar : labels.en}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="safety-loading">
          <Loader2 size={32} className="animate-spin" />
          <p>{isArabic ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      ) : error ? (
        <div className="safety-error">
          <AlertCircle size={32} />
          <p>{error}</p>
          <button onClick={fetchReportedAds} className="retry-btn">
            {isArabic ? 'إعادة المحاولة' : 'Retry'}
          </button>
        </div>
      ) : reportedAds.length === 0 ? (
        <div className="safety-empty">
          <Shield size={48} />
          <h3>{isArabic ? 'لا توجد بلاغات' : 'No Reports'}</h3>
          <p>{isArabic ? 'لم يتم الإبلاغ عن أي إعلانات حتى الآن' : 'No ads have been reported yet'}</p>
        </div>
      ) : (
        <div className="reported-ads-list">
          {reportedAds.map((item) => (
            <div 
              key={item.ad_id} 
              className="reported-ad-card"
              data-testid={`reported-ad-${item.ad_id}`}
            >
              {item.badge && (
                <div className={`ad-badge ${getBadgeStyle(item.badge)}`}>
                  {item.badge === 'auto_hidden' 
                    ? (isArabic ? 'مخفي تلقائياً' : 'Auto-hidden')
                    : (isArabic ? 'مشبوه' : 'Suspicious')
                  }
                </div>
              )}

              <div className="ad-info-section">
                <div className="ad-image">
                  {item.ad?.images?.[0] ? (
                    <img 
                      src={getImageUrl(item.ad.images[0])} 
                      alt={item.ad?.title}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div className="no-image">
                      <Image size={24} />
                    </div>
                  )}
                </div>
                
                <div className="ad-details">
                  <h3 className="ad-title">{item.ad?.title || 'Unknown Ad'}</h3>
                  <div className="ad-meta">
                    <span className="ad-status">
                      {item.ad?.status || 'unknown'}
                    </span>
                    <span className="report-count">
                      <AlertTriangle size={14} />
                      {item.report_count} {isArabic ? 'بلاغات' : 'reports'}
                    </span>
                  </div>
                  
                  <div className="reasons-tags">
                    {item.reasons?.map((reason) => (
                      <span key={reason} className="reason-tag">
                        {isArabic ? REPORT_REASONS[reason]?.ar : REPORT_REASONS[reason]?.en || reason}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {item.seller && (
                <div className="seller-section">
                  <User size={14} />
                  <span>{item.seller.name || item.seller.email}</span>
                  {item.seller.is_banned && (
                    <span className="banned-badge">
                      {isArabic ? 'محظور' : 'Banned'}
                    </span>
                  )}
                  {item.seller.trust_score !== undefined && (
                    <span className="trust-score">
                      Trust: {item.seller.trust_score}
                    </span>
                  )}
                </div>
              )}

              <div className="report-time">
                <Clock size={14} />
                <span>{formatDate(item.latest_report)}</span>
              </div>

              <div className="ad-actions">
                <button
                  onClick={() => openReportReview(item)}
                  className="action-btn review"
                  title={isArabic ? 'مراجعة البلاغات' : 'Review Reports'}
                  data-testid={`review-reports-${item.ad_id}`}
                >
                  <FileWarning size={16} />
                  <span>{isArabic ? 'مراجعة' : 'Review'}</span>
                </button>

                <button
                  onClick={() => handleAction(item.ad_id, 'keep_ad')}
                  disabled={actionLoading === item.ad_id}
                  className="action-btn keep"
                  title={isArabic ? 'الاحتفاظ بالإعلان' : 'Keep Ad'}
                  data-testid={`keep-ad-${item.ad_id}`}
                >
                  {actionLoading === item.ad_id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} />
                  )}
                  <span>{isArabic ? 'احتفاظ' : 'Keep'}</span>
                </button>
                
                <button
                  onClick={() => handleAction(item.ad_id, 'delete_ad')}
                  disabled={actionLoading === item.ad_id}
                  className="action-btn delete"
                  title={isArabic ? 'حذف الإعلان' : 'Delete Ad'}
                  data-testid={`delete-ad-${item.ad_id}`}
                >
                  <Trash2 size={16} />
                  <span>{isArabic ? 'حذف' : 'Delete'}</span>
                </button>
                
                <button
                  onClick={() => handleAction(item.ad_id, 'ban_user')}
                  disabled={actionLoading === item.ad_id || item.seller?.is_banned}
                  className="action-btn ban"
                  title={isArabic ? 'حظر البائع' : 'Ban User'}
                  data-testid={`ban-user-${item.ad_id}`}
                >
                  <Ban size={16} />
                  <span>{isArabic ? 'حظر' : 'Ban'}</span>
                </button>
                
                {item.ad?.status === 'hidden_auto' && (
                  <button
                    onClick={() => handleAction(item.ad_id, 'restore_ad')}
                    disabled={actionLoading === item.ad_id}
                    className="action-btn restore"
                    title={isArabic ? 'استعادة الإعلان' : 'Restore Ad'}
                    data-testid={`restore-ad-${item.ad_id}`}
                  >
                    <RotateCcw size={16} />
                    <span>{isArabic ? 'استعادة' : 'Restore'}</span>
                  </button>
                )}
                
                <a
                  href={`/#/ad/${item.ad_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-btn view"
                  title={isArabic ? 'عرض الإعلان' : 'View Ad'}
                >
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="safety-pagination">
          <button
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            className="pagination-btn"
          >
            {isArabic ? 'السابق' : 'Previous'}
          </button>
          <span className="pagination-info">
            {skip + 1} - {Math.min(skip + limit, total)} {isArabic ? 'من' : 'of'} {total}
          </span>
          <button
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
            className="pagination-btn"
          >
            {isArabic ? 'التالي' : 'Next'}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="safety-center-page" dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="safety-header">
        <div className="safety-header-content">
          <div className="safety-title-section">
            <Shield className="safety-icon" size={28} />
            <div>
              <h1 className="safety-title">
                {isArabic ? 'مركز السلامة' : 'Safety Center'}
              </h1>
              <p className="safety-subtitle">
                {isArabic ? 'إدارة الثقة والأمان' : 'Trust & Safety Management'}
              </p>
            </div>
          </div>
          
          <button 
            className="refresh-btn"
            onClick={() => {
              fetchDashboard();
              if (activeTab === 'reports') fetchReportedAds();
              if (activeTab === 'cases') fetchCases();
              if (activeTab === 'appeals') fetchAppeals();
            }}
            disabled={loading || dashboardLoading}
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className={(loading || dashboardLoading) ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="safety-tabs" data-testid="safety-tabs">
          <button 
            className={`safety-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
            data-testid="tab-dashboard"
          >
            <BarChart3 size={18} />
            <span>{isArabic ? 'لوحة التحكم' : 'Dashboard'}</span>
          </button>
          <button 
            className={`safety-tab ${activeTab === 'reports' ? 'active' : ''}`}
            onClick={() => setActiveTab('reports')}
            data-testid="tab-reports"
          >
            <AlertTriangle size={18} />
            <span>{isArabic ? 'البلاغات' : 'Reports'}</span>
            {total > 0 && <span className="tab-badge">{total}</span>}
          </button>
          <button 
            className={`safety-tab ${activeTab === 'cases' ? 'active' : ''}`}
            onClick={() => setActiveTab('cases')}
            data-testid="tab-cases"
          >
            <Users size={18} />
            <span>{isArabic ? 'الحالات' : 'Cases'}</span>
          </button>
          <button 
            className={`safety-tab ${activeTab === 'appeals' ? 'active' : ''}`}
            onClick={() => setActiveTab('appeals')}
            data-testid="tab-appeals"
          >
            <Scale size={18} />
            <span>{isArabic ? 'الاستئنافات' : 'Appeals'}</span>
            {dashboard?.metrics?.pending_appeals > 0 && (
              <span className="tab-badge">{dashboard.metrics.pending_appeals}</span>
            )}
          </button>
        </div>
      </div>

      {/* Pending Bans Alert */}
      {pendingBans.length > 0 && (
        <div className="pending-bans-alert" data-testid="pending-bans-alert">
          <div className="alert-content">
            <UserX size={20} />
            <span>
              {isArabic 
                ? `${pendingBans.length} مستخدم بانتظار تأكيد الحظر`
                : `${pendingBans.length} user(s) pending ban confirmation`
              }
            </span>
            <button 
              className="view-bans-btn"
              onClick={() => setShowPendingBans(!showPendingBans)}
            >
              {showPendingBans ? (isArabic ? 'إخفاء' : 'Hide') : (isArabic ? 'عرض' : 'View')}
            </button>
          </div>
          
          {showPendingBans && (
            <div className="pending-bans-list">
              {pendingBans.map((user) => (
                <div key={user.user_id} className="pending-ban-item">
                  <div className="ban-user-info">
                    <span className="ban-user-name">{user.name || user.email}</span>
                    <span className="ban-strikes">{user.report_strike_count} strikes</span>
                  </div>
                  <div className="ban-actions">
                    <button
                      onClick={() => handleBanConfirmation(user.user_id, true)}
                      disabled={banActionLoading === user.user_id}
                      className="confirm-ban-btn"
                      data-testid={`confirm-ban-${user.user_id}`}
                    >
                      {banActionLoading === user.user_id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Ban size={14} />
                      )}
                      {isArabic ? 'تأكيد الحظر' : 'Confirm Ban'}
                    </button>
                    <button
                      onClick={() => handleBanConfirmation(user.user_id, false)}
                      disabled={banActionLoading === user.user_id}
                      className="reject-ban-btn"
                      data-testid={`reject-ban-${user.user_id}`}
                    >
                      <X size={14} />
                      {isArabic ? 'رفض' : 'Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Content */}
      <div className="safety-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'cases' && renderCases()}
        {activeTab === 'appeals' && renderAppeals()}
      </div>

      {/* Report Review Modal */}
      {reviewModalOpen && selectedAd && (
        <div className="review-modal-overlay" onClick={() => setReviewModalOpen(false)}>
          <div 
            className="review-modal" 
            onClick={(e) => e.stopPropagation()}
            dir={isArabic ? 'rtl' : 'ltr'}
          >
            <div className="review-modal-header">
              <h2>{isArabic ? 'مراجعة البلاغات' : 'Review Reports'}</h2>
              <button 
                className="close-modal-btn"
                onClick={() => setReviewModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="review-modal-content">
              <div className="review-ad-info">
                <h3>{selectedAd.ad?.title || 'Unknown Ad'}</h3>
                <p className="report-count-info">
                  {selectedAd.report_count} {isArabic ? 'بلاغات على هذا الإعلان' : 'reports on this ad'}
                </p>
              </div>

              <div className="reports-list">
                {selectedAd.reports?.map((report) => (
                  <div key={report.report_id} className="report-item">
                    <div className="report-info">
                      <span className={`report-status-badge ${STATUS_LABELS[report.status]?.color || ''}`}>
                        {isArabic ? STATUS_LABELS[report.status]?.ar : STATUS_LABELS[report.status]?.en || report.status}
                      </span>
                      <span className="report-reason">
                        {isArabic ? REPORT_REASONS[report.reason]?.ar : REPORT_REASONS[report.reason]?.en || report.reason}
                      </span>
                      <span className="report-type">
                        {report.reporter_type === 'user' ? '👤' : '👻'} 
                        {report.reporter_type === 'user' ? (isArabic ? 'مستخدم' : 'User') : (isArabic ? 'ضيف' : 'Guest')}
                      </span>
                    </div>
                    
                    {report.description && (
                      <p className="report-description">{report.description}</p>
                    )}

                    {report.evidence_url && (
                      <a 
                        href={getImageUrl(report.evidence_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="evidence-link"
                      >
                        {isArabic ? 'عرض الدليل' : 'View Evidence'}
                      </a>
                    )}

                    {(report.status === 'pending' || report.status === 'new') && (
                      <div className="report-review-actions">
                        <button
                          onClick={() => handleReviewReport(report.report_id, 'dismissed')}
                          disabled={reviewLoading === report.report_id}
                          className="dismiss-btn"
                          title={isArabic ? 'رفض البلاغ (بدون عقوبة)' : 'Dismiss (no penalty)'}
                          data-testid={`dismiss-${report.report_id}`}
                        >
                          {reviewLoading === report.report_id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <X size={14} />
                          )}
                          {isArabic ? 'رفض' : 'Dismiss'}
                        </button>
                        
                        <button
                          onClick={() => handleReviewReport(report.report_id, 'false_malicious')}
                          disabled={reviewLoading === report.report_id || report.reporter_type !== 'user'}
                          className="strike-btn"
                          title={isArabic ? 'بلاغ كاذب (تطبيق إنذار)' : 'False report (apply strike)'}
                          data-testid={`strike-${report.report_id}`}
                        >
                          <AlertTriangle size={14} />
                          {isArabic ? 'إنذار' : 'Strike'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="review-modal-footer">
                <p className="strike-info">
                  {isArabic 
                    ? '⚠️ الإنذار يُطبق فقط على البلاغات الكاذبة عمداً من المستخدمين المسجلين'
                    : '⚠️ Strikes only apply to intentionally false reports from registered users'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Case Detail Drawer */}
      {caseDetailOpen && selectedCase && (
        <div className="case-drawer-overlay" onClick={() => setCaseDetailOpen(false)}>
          <div 
            className="case-drawer" 
            onClick={(e) => e.stopPropagation()}
            dir={isArabic ? 'rtl' : 'ltr'}
          >
            <div className="case-drawer-header">
              <h2>{isArabic ? 'تفاصيل الحالة' : 'Case Details'}</h2>
              <button 
                className="close-drawer-btn"
                onClick={() => setCaseDetailOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            {caseDetailLoading ? (
              <div className="case-drawer-loading">
                <Loader2 className="animate-spin" size={32} />
              </div>
            ) : (
              <div className="case-drawer-content">
                {/* User Info */}
                <div className="case-user-card">
                  <div className="case-user-header">
                    <div className="user-avatar">
                      {selectedCase.case.avatar ? (
                        <img src={toAbsoluteUrl(selectedCase.case.avatar)} alt="" />
                      ) : (
                        <User size={24} />
                      )}
                    </div>
                    <div className="user-details">
                      <h3>{selectedCase.case.name || 'Unknown'}</h3>
                      <p>{selectedCase.case.email}</p>
                    </div>
                    <div className={`trust-badge ${TRUST_LEVEL_COLORS[selectedCase.case.trust_level]}`}>
                      <span className="trust-score-big">{selectedCase.case.trust_score}</span>
                      <span className="trust-label">{selectedCase.case.trust_level}</span>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="case-stats-grid">
                    <div className="case-stat">
                      <span className="stat-value">{selectedCase.stats.total_ads}</span>
                      <span className="stat-label">{isArabic ? 'الإعلانات' : 'Ads'}</span>
                    </div>
                    <div className="case-stat">
                      <span className="stat-value">{selectedCase.stats.hidden_ads}</span>
                      <span className="stat-label">{isArabic ? 'مخفية' : 'Hidden'}</span>
                    </div>
                    <div className="case-stat">
                      <span className="stat-value">{selectedCase.stats.reports_against}</span>
                      <span className="stat-label">{isArabic ? 'بلاغات ضده' : 'Reports'}</span>
                    </div>
                    <div className="case-stat">
                      <span className="stat-value">{selectedCase.case.report_strike_count || 0}</span>
                      <span className="stat-label">{isArabic ? 'إنذارات' : 'Strikes'}</span>
                    </div>
                  </div>

                  {/* Account Status */}
                  {selectedCase.case.account_status !== 'active' && (
                    <div className="account-status-alert">
                      {selectedCase.case.account_status === 'suspended' && (
                        <div className="status-suspended">
                          <Pause size={16} />
                          <span>{isArabic ? 'موقوف حتى' : 'Suspended until'}: {formatDate(selectedCase.case.suspended_until)}</span>
                        </div>
                      )}
                      {selectedCase.case.account_status === 'pending_ban' && (
                        <div className="status-pending-ban">
                          <AlertOctagon size={16} />
                          <span>{isArabic ? 'بانتظار تأكيد الحظر' : 'Pending ban confirmation'}</span>
                        </div>
                      )}
                      {selectedCase.case.is_banned && (
                        <div className="status-banned">
                          <Ban size={16} />
                          <span>{isArabic ? 'محظور' : 'Banned'}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="case-actions-card">
                  <h4>{isArabic ? 'إجراءات سريعة' : 'Quick Actions'}</h4>
                  <div className="case-actions-grid">
                    <button
                      onClick={() => handleCaseAction(selectedCase.case.user_id, 'warn')}
                      disabled={caseActionLoading}
                      className="case-action-btn warn"
                      data-testid="case-action-warn"
                    >
                      <AlertTriangle size={16} />
                      {isArabic ? 'تحذير' : 'Warn'}
                    </button>
                    <button
                      onClick={() => handleCaseAction(selectedCase.case.user_id, 'cooldown', null, null, 2)}
                      disabled={caseActionLoading}
                      className="case-action-btn cooldown"
                      data-testid="case-action-cooldown"
                    >
                      <Pause size={16} />
                      {isArabic ? 'تبريد' : 'Cooldown'}
                    </button>
                    <button
                      onClick={() => handleCaseAction(selectedCase.case.user_id, 'suspend', null, null, 48)}
                      disabled={caseActionLoading}
                      className="case-action-btn suspend"
                      data-testid="case-action-suspend"
                    >
                      <AlertOctagon size={16} />
                      {isArabic ? 'إيقاف' : 'Suspend'}
                    </button>
                    <button
                      onClick={() => handleCaseAction(selectedCase.case.user_id, 'ban')}
                      disabled={caseActionLoading || selectedCase.case.is_banned}
                      className="case-action-btn ban"
                      data-testid="case-action-ban"
                    >
                      <Ban size={16} />
                      {isArabic ? 'حظر' : 'Ban'}
                    </button>
                    {selectedCase.case.account_status !== 'active' && (
                      <button
                        onClick={() => handleCaseAction(selectedCase.case.user_id, 'restore')}
                        disabled={caseActionLoading}
                        className="case-action-btn restore"
                        data-testid="case-action-restore"
                      >
                        <RotateCcw size={16} />
                        {isArabic ? 'استعادة' : 'Restore'}
                      </button>
                    )}
                    <button
                      onClick={() => handleCaseAction(selectedCase.case.user_id, 'reset_trust')}
                      disabled={caseActionLoading || selectedCase.case.trust_score === 100}
                      className="case-action-btn reset"
                      data-testid="case-action-reset-trust"
                    >
                      <RefreshCw size={16} />
                      {isArabic ? 'إعادة الثقة' : 'Reset Trust'}
                    </button>
                  </div>
                </div>

                {/* Trust Events */}
                {selectedCase.trust_events?.length > 0 && (
                  <div className="case-section">
                    <h4>
                      <History size={16} />
                      {isArabic ? 'أحداث الثقة' : 'Trust Events'}
                    </h4>
                    <div className="events-list">
                      {selectedCase.trust_events.slice(0, 5).map((event, idx) => (
                        <div key={idx} className="event-item">
                          <span className={`event-delta ${event.score_delta > 0 ? 'positive' : 'negative'}`}>
                            {event.score_delta > 0 ? '+' : ''}{event.score_delta}
                          </span>
                          <span className="event-reason">{event.reason_code}</span>
                          <span className="event-date">{formatDate(event.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enforcement Actions */}
                {selectedCase.enforcement_actions?.length > 0 && (
                  <div className="case-section">
                    <h4>
                      <AlertOctagon size={16} />
                      {isArabic ? 'إجراءات التنفيذ' : 'Enforcement Actions'}
                    </h4>
                    <div className="actions-list">
                      {selectedCase.enforcement_actions.slice(0, 5).map((action, idx) => (
                        <div key={idx} className="enforcement-item">
                          <span className={`action-type ${action.action_type}`}>{action.action_type}</span>
                          <span className="action-status">{action.status}</span>
                          <span className="action-date">{formatDate(action.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSafetyCenter;
